import React, { useState, useEffect } from "react";
import {
  MoreVertical,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Trash2,
  Eye,
  Star,
  Flag,
  Search,
  Filter,
  ArrowUpDown,
} from "lucide-react";
import { useToast } from "../components/ui/ToastContainer";
import { reviewService } from "../services/reviewService";
import { Button } from "../components/ui/Button";
import { Modal } from "../components/ui/Modal";
import { Input } from "../components/ui/Input";
import { Card } from "../components/ui/Card";
import { DashboardLayout } from "../layouts/DashboardLayout";

export default function ReviewsPage() {
  const { showSuccess, showError } = useToast();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [filters, setFilters] = useState({
    reviewType: "",
    isFlagged: "",
    isApproved: "",
  });
  const [isAnonymousFilter, setIsAnonymousFilter] = useState("all");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [sortField, setSortField] = useState("createdAt");

  const [selectedReview, setSelectedReview] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  // Search is not implemented in backend yet based on previous code,
  // but we add the UI for consistency.
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchReviews = async (page = 1) => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: 10,
        ...filters,
        ...(isAnonymousFilter !== "all" && {
          isAnonymous: isAnonymousFilter === "anonymous",
        }),
        search: debouncedSearchTerm,
      };
      // Remove empty filters
      Object.keys(params).forEach(
        (key) => params[key] === "" && delete params[key],
      );

      const response = await reviewService.getAllReviews(params);
      if (response.status === "success") {
        setReviews(response.reviews);
        setPagination(response.pagination);
      }
    } catch (error) {
      showError("Failed to fetch reviews");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews(1);
  }, [filters, debouncedSearchTerm, isAnonymousFilter]);

  const handlePageChange = (newPage) => {
    fetchReviews(newPage);
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setIsFilterOpen(false);
  };

  const handleDelete = async (reviewId) => {
    if (confirm("Are you sure you want to delete this review?")) {
      try {
        await reviewService.deleteReview(reviewId);
        showSuccess("Review deleted");
        fetchReviews(pagination.page);
        setIsDetailOpen(false);
      } catch (error) {
        showError("Failed to delete review");
      }
    }
  };

  const handleFlag = async (reviewId) => {
    const reason = prompt("Enter reason for flagging:");
    if (reason) {
      try {
        await reviewService.flagReview(reviewId, reason);
        showSuccess("Review flagged");
        fetchReviews(pagination.page);
        setIsDetailOpen(false);
      } catch (error) {
        showError("Failed to flag review");
      }
    }
  };

  const handleUnflag = async (reviewId) => {
    if (confirm("Are you sure you want to remove the flag from this review?")) {
      try {
        await reviewService.unflagReview(reviewId);
        showSuccess("Review unflagged");
        fetchReviews(pagination.page);
        setIsDetailOpen(false);
        // Force refresh table
        fetchReviews(pagination.page);
      } catch (error) {
        showError("Failed to unflag review");
      }
    }
  };

  const openDetail = (review) => {
    setSelectedReview(review);
    setIsDetailOpen(true);
  };

  return (
    <DashboardLayout>
      <div className="p-3 sm:p-6 space-y-4 sm:space-y-6">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-bold tracking-tight text-gray-900">
            Reviews
          </h1>
          <p className="text-gray-500">Manage and moderate user reviews.</p>
        </div>

        <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4">
          {/* Search input - Placeholder for consistency */}
          <div className="w-full xl:w-[408px]">
            <Input
              placeholder="Search by user, email, course or content..."
              icon={<Search size={20} />}
              className="bg-white w-full"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full xl:w-auto">
            <div className="grid grid-cols-2 sm:flex items-center gap-2 w-full sm:w-auto">
              {/* Filter Button */}
              <div className="relative w-full sm:w-auto">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-[44px] sm:h-[39px] px-3 w-full sm:w-auto justify-center touch-target"
                  onClick={() => {
                    setIsFilterOpen(!isFilterOpen);
                    setIsSortOpen(false);
                  }}
                >
                  <Filter size={16} /> Filter
                </Button>
                {isFilterOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setIsFilterOpen(false)}
                    />
                    <div className="absolute left-0 sm:left-auto sm:right-0 top-full mt-1 z-20 bg-white border border-gray-100 rounded-lg shadow-lg p-3 min-w-[240px] w-full sm:w-max animate-in fade-in zoom-in duration-150">
                      {/* Type Filter */}
                      <p className="text-xs font-semibold uppercase text-gray-500 mb-2">
                        Review Type
                      </p>
                      <div className="flex flex-col gap-1">
                        <button
                          onClick={() => handleFilterChange("reviewType", "")}
                          className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-50 ${
                            filters.reviewType === ""
                              ? "bg-gray-100 font-bold"
                              : "text-gray-700"
                          }`}
                        >
                          All Types
                        </button>
                        <button
                          onClick={() =>
                            handleFilterChange("reviewType", "course")
                          }
                          className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-50 ${
                            filters.reviewType === "course"
                              ? "bg-gray-100 font-bold"
                              : "text-gray-700"
                          }`}
                        >
                          Course
                        </button>
                        <button
                          onClick={() =>
                            handleFilterChange("reviewType", "blog")
                          }
                          className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-50 ${
                            filters.reviewType === "blog"
                              ? "bg-gray-100 font-bold"
                              : "text-gray-700"
                          }`}
                        >
                          Blog
                        </button>
                        <button
                          onClick={() =>
                            handleFilterChange("reviewType", "instructor")
                          }
                          className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-50 ${
                            filters.reviewType === "instructor"
                              ? "bg-gray-100 font-bold"
                              : "text-gray-700"
                          }`}
                        >
                          Instructor
                        </button>
                      </div>

                      <div className="border-t border-gray-100 my-2"></div>

                      {/* Status Filter */}
                      <p className="text-xs font-semibold uppercase text-gray-500 mb-2">
                        Approval Status
                      </p>
                      <div className="flex flex-col gap-1">
                        <button
                          onClick={() => handleFilterChange("isApproved", "")}
                          className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-50 ${
                            filters.isApproved === ""
                              ? "bg-gray-100 font-bold"
                              : "text-gray-700"
                          }`}
                        >
                          All
                        </button>
                        <button
                          onClick={() =>
                            handleFilterChange("isApproved", "true")
                          }
                          className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-50 ${
                            filters.isApproved === "true"
                              ? "bg-green-50 font-bold text-green-700"
                              : "text-gray-700"
                          }`}
                        >
                          Approved
                        </button>
                        <button
                          onClick={() =>
                            handleFilterChange("isApproved", "false")
                          }
                          className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-50 ${
                            filters.isApproved === "false"
                              ? "bg-yellow-50 font-bold text-yellow-700"
                              : "text-gray-700"
                          }`}
                        >
                          Pending/Rejected
                        </button>
                      </div>

                      <div className="border-t border-gray-100 my-2"></div>

                      {/* Flag Filter */}
                      <p className="text-xs font-semibold uppercase text-gray-500 mb-2">
                        Flag Status
                      </p>
                      <div className="flex flex-col gap-1">
                        <button
                          onClick={() => handleFilterChange("isFlagged", "")}
                          className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-50 ${
                            filters.isFlagged === ""
                              ? "bg-gray-100 font-bold"
                              : "text-gray-700"
                          }`}
                        >
                          All
                        </button>
                        <button
                          onClick={() =>
                            handleFilterChange("isFlagged", "true")
                          }
                          className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-50 ${
                            filters.isFlagged === "true"
                              ? "bg-red-50 font-bold text-red-700"
                              : "text-gray-700"
                          }`}
                        >
                          Flagged
                        </button>
                        <button
                          onClick={() =>
                            handleFilterChange("isFlagged", "false")
                          }
                          className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-50 ${
                            filters.isFlagged === "false"
                              ? "bg-gray-100 font-bold"
                              : "text-gray-700"
                          }`}
                        >
                          Clean
                        </button>
                      </div>

                      <div className="border-t border-gray-100 my-2"></div>

                      {/* Reviewer Filter */}
                      <p className="text-xs font-semibold uppercase text-gray-500 mb-2">
                        Reviewer
                      </p>
                      <div className="flex flex-col gap-1">
                        <button
                          onClick={() => {
                            setIsAnonymousFilter("all");
                            setIsFilterOpen(false);
                          }}
                          className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-50 ${
                            isAnonymousFilter === "all"
                              ? "bg-gray-100 font-bold"
                              : "text-gray-700"
                          }`}
                        >
                          All reviews
                        </button>
                        <button
                          onClick={() => {
                            setIsAnonymousFilter("anonymous");
                            setIsFilterOpen(false);
                          }}
                          className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-50 ${
                            isAnonymousFilter === "anonymous"
                              ? "bg-purple-50 font-bold text-purple-700"
                              : "text-gray-700"
                          }`}
                        >
                          Anonymous only
                        </button>
                        <button
                          onClick={() => {
                            setIsAnonymousFilter("authenticated");
                            setIsFilterOpen(false);
                          }}
                          className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-50 ${
                            isAnonymousFilter === "authenticated"
                              ? "bg-gray-100 font-bold"
                              : "text-gray-700"
                          }`}
                        >
                          Authenticated only
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Sort Button - Placeholder */}
              <div className="relative w-full sm:w-auto">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-[44px] sm:h-[39px] px-3 w-full sm:w-auto justify-center touch-target"
                  onClick={() => {
                    setIsSortOpen(!isSortOpen);
                    setIsFilterOpen(false);
                  }}
                >
                  <ArrowUpDown size={16} /> Sort By
                </Button>
                {isSortOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setIsSortOpen(false)}
                    />
                    <div className="absolute right-0 top-full mt-1 z-20 bg-white border border-gray-100 rounded-lg shadow-lg p-1 min-w-[150px] animate-in fade-in zoom-in duration-150">
                      <button
                        onClick={() => {
                          setSortField("createdAt");
                          setIsSortOpen(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-50 ${
                          sortField === "createdAt"
                            ? "bg-gray-100 font-bold"
                            : ""
                        }`}
                      >
                        Recency (Default)
                      </button>
                      {/* Add more sort options if backend supports them */}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        <Card className="p-0 border shadow-sm w-full overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-gray-500 whitespace-nowrap">
              <thead className="bg-gray-50 text-gray-700 uppercase text-xs border-b border-gray-200">
                <tr>
                  <th className="px-3 sm:px-6 py-4 font-semibold">User</th>
                  <th className="px-3 sm:px-6 py-4 font-semibold">Item</th>
                  <th className="px-3 sm:px-6 py-4 font-semibold">Rating</th>
                  <th className="px-3 sm:px-6 py-4 font-semibold">Comment</th>
                  <th className="px-3 sm:px-6 py-4 font-semibold">Status</th>
                  <th className="px-3 sm:px-6 py-4 font-semibold">Date</th>
                  <th className="px-3 sm:px-6 py-4 font-semibold text-right">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-3 sm:px-6 py-8 text-center">
                      <div className="animate-spin h-6 w-6 border-2 border-gold border-t-transparent rounded-full mx-auto"></div>
                    </td>
                  </tr>
                ) : reviews.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-3 sm:px-6 py-8 text-center">
                      No reviews found.
                    </td>
                  </tr>
                ) : (
                  reviews.map((review) => (
                    <tr
                      key={review._id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-3 sm:px-6 py-4">
                        <div className="flex items-center gap-3">
                          {review.isAnonymous ? (
                            <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                              <span className="text-xs font-bold text-gray-400 uppercase">
                                {(review.displayName || "A").charAt(0)}
                              </span>
                            </div>
                          ) : review.user?.profilePicture ? (
                            <img
                              src={review.user.profilePicture}
                              alt=""
                              className="w-8 h-8 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                              <span className="text-xs font-bold text-gray-500 uppercase">
                                {review.user?.firstName?.charAt(0) || "U"}
                              </span>
                            </div>
                          )}
                          <div className="flex flex-col">
                            <div className="font-medium text-gray-900 flex items-center gap-2">
                              {review.isAnonymous ? (
                                <>
                                  <span>
                                    {review.displayName || "Anonymous"}
                                  </span>
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                    Anonymous
                                  </span>
                                </>
                              ) : (
                                <span>
                                  {review.user?.firstName}{" "}
                                  {review.user?.lastName}
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-gray-500">
                              {review.isAnonymous
                                ? "Hidden for privacy"
                                : review.user?.email || "No email"}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 sm:px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-gray-900 font-medium truncate max-w-[150px]">
                            {typeof review.reviewedItem === "object"
                              ? review.reviewedItem?.title ||
                                review.reviewedItem?.name
                              : "Unknown Item"}
                          </span>
                          <span className="text-xs uppercase text-gray-400">
                            {review.reviewType}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 sm:px-6 py-4">
                        <div className="flex text-amber-400">
                          {Array.from({ length: review.rating }).map((_, i) => (
                            <Star key={i} className="w-3 h-3 fill-current" />
                          ))}
                        </div>
                      </td>
                      <td
                        className="px-3 sm:px-6 py-4 max-w-xs truncate"
                        title={review.comment}
                      >
                        {review.comment}
                      </td>
                      <td className="px-3 sm:px-6 py-4">
                        {review.isFlagged ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            Flagged
                          </span>
                        ) : review.isApproved ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Approved
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            Pending
                          </span>
                        )}
                      </td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                        {new Date(review.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-3 sm:px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openDetail(review)}
                            title="View Details"
                          >
                            <Eye className="h-4 w-4 text-blue-500" />
                          </Button>
                          {!review.isFlagged ? (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleFlag(review._id)}
                              title="Flag"
                            >
                              <Flag className="h-4 w-4 text-amber-500" />
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleUnflag(review._id)}
                              title="Unflag"
                            >
                              <CheckCircle className="h-4 w-4 text-green-500" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(review._id)}
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
              <span className="text-sm text-gray-500">
                Page {pagination.page} of {pagination.pages}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page <= 1}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page >= pagination.pages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </Card>

        <Modal
          isOpen={isDetailOpen}
          onClose={() => setIsDetailOpen(false)}
          title="Review Details"
        >
          {selectedReview && (
            <div className="space-y-4">
              <div className="flex items-center gap-4 border-b border-gray-200 pb-4">
                <div className="bg-gray-100 p-3 rounded-lg text-center min-w-[80px]">
                  <span className="text-gray-500 text-xs uppercase block mb-1">
                    Rating
                  </span>
                  <div className="flex justify-center text-amber-400">
                    {Array.from({ length: selectedReview.rating }).map(
                      (_, i) => (
                        <Star key={i} className="w-4 h-4 fill-current" />
                      ),
                    )}
                  </div>
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                    {selectedReview.isAnonymous ? (
                      <>
                        <span>
                          {selectedReview.displayName || "Anonymous"}
                        </span>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                          Anonymous
                        </span>
                      </>
                    ) : (
                      <span>
                        {selectedReview.user?.firstName}{" "}
                        {selectedReview.user?.lastName}
                      </span>
                    )}
                  </h4>
                  <p className="text-sm text-gray-500">
                    Reviewed{" "}
                    <span className="font-medium text-gray-700">
                      {typeof selectedReview.reviewedItem === "object"
                        ? selectedReview.reviewedItem?.title ||
                          selectedReview.reviewedItem?.name
                        : "Item"}
                    </span>
                    <br />
                    on {new Date(selectedReview.createdAt).toLocaleString()}
                  </p>
                </div>
                {selectedReview.isVerifiedPurchase && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                    {" "}
                    Verified{" "}
                  </span>
                )}
              </div>

              <div className="space-y-2">
                <span className="text-sm font-medium text-gray-700 uppercase">
                  Content
                </span>
                <p className="bg-gray-50 p-4 rounded-lg border border-gray-200 text-gray-700 whitespace-pre-wrap">
                  {selectedReview.comment}
                </p>
              </div>

              {selectedReview.screenshots &&
                selectedReview.screenshots.length > 0 && (
                  <div className="space-y-2">
                    <span className="text-sm font-medium text-gray-700 uppercase">
                      Screenshots
                    </span>
                    <div className="flex gap-2 overflow-x-auto pb-2">
                      {selectedReview.screenshots.map((s, i) => (
                        <a
                          key={i}
                          href={s.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block relative h-24 w-24 rounded-lg overflow-hidden border border-gray-200 hover:border-gold transition-colors"
                        >
                          <img
                            src={s.url}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        </a>
                      ))}
                    </div>
                  </div>
                )}

              {selectedReview.isAnonymous && selectedReview.contactEmail && (
                <div className="border-t border-gray-200 pt-3 mt-3">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                    Contact email (anonymous reviewer)
                  </p>
                  <a
                    href={`mailto:${selectedReview.contactEmail}`}
                    className="text-blue-600 hover:underline break-all"
                  >
                    {selectedReview.contactEmail}
                  </a>
                  <p className="text-xs text-gray-400 mt-1">
                    Visible to admins only. Used for follow-up, never shown
                    publicly.
                  </p>
                </div>
              )}

              {selectedReview.isFlagged && (
                <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                  <span className="text-red-800 font-semibold mb-1 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Flagged for Moderation
                  </span>
                  <p className="text-sm text-red-700">
                    Reason: {selectedReview.flagReason}
                  </p>
                  <div className="text-xs text-red-500 mt-2">
                    Flagged by Admin ID:{" "}
                    {selectedReview.moderatedBy?._id ||
                      selectedReview.moderatedBy}
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-4 border-t border-gray-200 mt-4">
                {!selectedReview.isFlagged ? (
                  <Button
                    variant="outline"
                    onClick={() => handleFlag(selectedReview._id)}
                    className="text-amber-600 border-amber-200 hover:bg-amber-50"
                  >
                    Flag
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    onClick={() => handleUnflag(selectedReview._id)}
                    className="text-green-600 border-green-200 hover:bg-green-50"
                  >
                    Unflag
                  </Button>
                )}
                <Button
                  variant="destructive"
                  onClick={() => handleDelete(selectedReview._id)}
                >
                  Delete
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => setIsDetailOpen(false)}
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </Modal>
      </div>
    </DashboardLayout>
  );
}
